import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, Building2, Church, Clock, Globe, HandHeart,
  Mail, MapPin, Phone, Send, Sparkles, Trash2, Check,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { church: Church, business: Building2, ministry: HandHeart };

export default function DirectoryDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/directory/${id}`);
      setD(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Listing not found");
      navigate("/directory");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const approve = async () => {
    try {
      await api.post(`/directory/${id}/approve`);
      toast.success("Approved");
      load();
    } catch { toast.error("Could not approve"); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${d.name}" from the directory?`)) return;
    try {
      await api.delete(`/directory/${id}`);
      toast.success("Removed");
      navigate("/directory");
    } catch { toast.error("Could not remove"); }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-[var(--jj-muted)]">Loading…</div>;
  if (!d) return null;

  const Icon = ICONS[d.kind] || Sparkles;
  const isAdmin = user?.role === "admin";
  const isOwner = user?.id && d.owner_user_id === user.id;

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-8 py-10">
      <Link to="/directory" className="inline-flex items-center gap-1.5 text-sm text-jjblue/80 hover:text-burgundy mb-6" data-testid="dir-back">
        <ArrowLeft className="h-4 w-4" /> Back to directory
      </Link>

      <div className="jj-card overflow-hidden">
        <div className="relative bg-gradient-to-br from-jjblue via-burgundy to-jjblue p-8 sm:p-10 text-cream">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-2xl bg-cream/15 border border-cream/20 flex items-center justify-center shrink-0">
              <Icon className="h-7 w-7 text-gold" strokeWidth={1.4} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-cream/70 flex items-center gap-2 flex-wrap">
                <span className="capitalize">{d.kind}</span>
                {d.status !== "approved" && (
                  <span className="px-2 py-0.5 rounded-full bg-gold text-jjblue font-semibold">{d.status}</span>
                )}
                {d.featured && <span className="px-2 py-0.5 rounded-full bg-gold/30 text-gold">Featured</span>}
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl mt-1 leading-tight" data-testid="dir-detail-name">{d.name}</h1>
              {(d.city || d.state) && (
                <div className="mt-2 text-sm text-cream/80 inline-flex items-center gap-1">
                  <MapPin className="h-4 w-4" /> {[d.city, d.state].filter(Boolean).join(", ")}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 sm:p-8 space-y-5">
          {d.description && <p className="text-jjblue/85 whitespace-pre-wrap leading-relaxed">{d.description}</p>}

          <div className="grid sm:grid-cols-2 gap-3">
            {d.address && (
              <div className="flex items-start gap-3 bg-cream-surface rounded-xl p-4">
                <MapPin className="h-4 w-4 text-burgundy mt-0.5" />
                <span className="text-sm text-jjblue">{d.address}</span>
              </div>
            )}
            {d.service_times && (
              <div className="flex items-start gap-3 bg-cream-surface rounded-xl p-4">
                <Clock className="h-4 w-4 text-burgundy mt-0.5" />
                <span className="text-sm text-jjblue">{d.service_times}</span>
              </div>
            )}
            {d.website && (
              <a href={d.website} target="_blank" rel="noreferrer" className="flex items-start gap-3 bg-cream-surface rounded-xl p-4 hover:border-gold border border-transparent transition-colors" data-testid="dir-website">
                <Globe className="h-4 w-4 text-burgundy mt-0.5" />
                <span className="text-sm text-jjblue truncate">{d.website.replace(/^https?:\/\//, "")}</span>
              </a>
            )}
            {d.phone && (
              <a href={`tel:${d.phone}`} className="flex items-start gap-3 bg-cream-surface rounded-xl p-4">
                <Phone className="h-4 w-4 text-burgundy mt-0.5" />
                <span className="text-sm text-jjblue">{d.phone}</span>
              </a>
            )}
            {d.email && (
              <a href={`mailto:${d.email}`} className="flex items-start gap-3 bg-cream-surface rounded-xl p-4">
                <Mail className="h-4 w-4 text-burgundy mt-0.5" />
                <span className="text-sm text-jjblue truncate">{d.email}</span>
              </a>
            )}
          </div>

          {d.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {d.tags.map((t) => (
                <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-cream-surface border border-jj text-jjblue/80">#{t}</span>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-3 pt-3 border-t border-jj">
            {d.owner_user_id && d.owner_name && (
              <div className="text-xs text-[var(--jj-muted)]">
                Submitted by <Link to={`/u/${d.owner_user_id}`} className="text-jjblue hover:text-burgundy hover:underline">{d.owner_name}</Link>
                {!isOwner && user && (
                  <>
                    {" · "}
                    <Link to={`/messages/${d.owner_user_id}`} className="text-burgundy hover:underline inline-flex items-center gap-1" data-testid="dir-message-owner">
                      <Send className="h-3 w-3" /> Message
                    </Link>
                  </>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 ml-auto">
              {isAdmin && d.status !== "approved" && (
                <button onClick={approve} className="btn-gold !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="dir-approve">
                  <Check className="h-3.5 w-3.5" /> Approve
                </button>
              )}
              {(isAdmin || isOwner) && (
                <button onClick={remove} className="text-xs text-burgundy hover:text-[var(--jj-burgundy-hover)] inline-flex items-center gap-1" data-testid="dir-delete">
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
